import './index.css';
import Card from '../card';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showErrorToast, hideLoader, showToast } from '../../actions/app-actions-types';
import { uploadPANImage } from '../../actions/more-actions-types';
import { verifyPAN, editUserProfile } from '../../actions/user-actions-types';
import EventEmitter from '../../utilities/EventEmitter';
import useTheme from '../../utilities/hooks/theme';
import I18n from '../../utilities/locale';
import Regex from '../../utilities/Regex';
import View from '../view';
import Text from '../text';
import Constants from '../../constants';
import Button from '@mui/material/Button/Button';
import PanDetails from '../pan_details';
import TextField from '@mui/material/TextField/TextField';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import React from 'react';
import DatePicker from '@mui/lab/DatePicker';
import { bytesToMegaBytes, documentMatchName, isOverEighteen } from '../../utilities/helper';
import moment from 'moment';
import { updatePan } from '../../actions/user-actions-types';

interface Props {
  list: any;
  onPressDelete: any;
  bankList: any;
}

const VerifyPanItem = ({ list, onPressDelete, bankList }: Props) => {
  let accountName = '';
  if (bankList.length > 0) {
    accountName = bankList[0].accountName;
  }
  let minDOB = new Date();
  minDOB.setMonth(minDOB.getMonth() - 12 * 10);
  const { ComponentTheme: theme } = useTheme();
  const dispatch = useDispatch();
  const [imagePath, setImagePath] = useState('' as any);
  const [expand, setExpand] = useState(false);
  const [name, setName] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [dob, setDOB] = useState(null as any);
  const [nameError, setNameError] = useState(null);
  const [panNumberError, setPanNumberError] = useState(null);
  const [panImageError, setPanImageError] = useState(null);
  const [dobError, setDOBError] = useState(null);
  const [isNeedToAdd, setIsNeedToAdd] = useState(false);
  const [item = {}] = list;
  const { number = '', status = '', _id = '' } = item;

  useEffect(() => {
    setIsNeedToAdd(status != 'verified' && number.length == 0);
    setExpand(false);
  }, [status, number]);

  const refName = useRef(null);
  const refCardNumber = useRef(null as any);
  const refDOB = useRef(null);

  const onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    if (Regex.validateName(event.target.value) || event.target.value === '') {
      setName(event.target.value);
      if (nameError != null) setNameError(null);
    }
  };

  const onChangePanNumber = (event: ChangeEvent<HTMLInputElement>) => {
    if (Regex.validateAlphaNumeric(event.target.value) || event.target.value === '') {
      setPanNumber(event.target.value);
      if (panNumberError != null) setPanNumberError(null);
    }
  };

  const onChangeUploadImage = (event: any) => {
    setImagePath(event.target.files[0]);
    if (panNumberError) {
      setPanImageError(null);
    }
  };

  const handleDatePicked = (date: Date | null | undefined) => {
    setDOB(date);
    if (dobError != null) setDOBError(null);
  };

  const updatePAN_detailProceed = (dob: any) => {
    const data = {
      name: name.toUpperCase().trim(),
      number: panNumber.toUpperCase().trim(),
      key: ''
    };
    dispatch(
      verifyPAN({
        data,
        callback: ({ error = {}, result = {} }: { error: any; result: any }) => {
          if (!error && result) {
            const { status = '' } = result;
            if (status === 'rejected') {
              dispatch(
                showErrorToast(
                  'Name Mismatched, Please provide correct name as mentioned in pan card'
                )
              );
            } else {
              setExpand(false);
              dispatch(showToast(I18n.t('PAN_DetailsSent')));
              dispatch(updatePan(result));
              uploadImage(result._id);
              onUpdateDOB(dob);
            }
          } else {
            const { message = '' } = error;
            if (message == 'No Record Found') {
              dispatch(showErrorToast('Please provide valid pan card number'));
            }
          }
        }
      })
    );
  };

  const uploadImage = (_id: string) => {
    if (_id) {
      const name: string = imagePath.name;
      dispatch(
        uploadPANImage({
          data: {
            file: imagePath,
            name: `${_id + name.substring(name.lastIndexOf('.'), name.length)}`
          },
          callback: ({ error }: { error: any }) => {
            dispatch(hideLoader());
            if (error) {
              dispatch(showErrorToast(I18n.t('ImageUploadFailed')));
            }
          }
        })
      );
    }
  };

  const onUpdateDOB = (dob: any) => {
    const data = {
      dob
    };
    dispatch(
      editUserProfile({
        data,
        callback: ({ error }: { error: any }) => {
          if (!error) {
            EventEmitter.emit('UserInfo');
          }
        }
      })
    );
  };

  const onPressSubmitButton = () => {
    let error = false,
      nameError = null,
      panNumberError = null,
      dobError = null,
      panImageError = null;
    const formattedDateDOB: any = moment(dob).format('YYYY-MM-DD');

    if (!name) {
      nameError = I18n.t('NameMissing');
      error = true;
    } else if (documentMatchName(accountName, name)) {
      nameError = I18n.t('AccountShouldMatchNameOnPAN');
      error = true;
    }

    if (!panNumber) {
      panNumberError = I18n.t('MissingPANNumber');
      error = true;
    } else if (!Regex.validatePAN(panNumber)) {
      panNumberError = I18n.t('InvalidPANNumber');
      error = true;
    }

    if (!formattedDateDOB || !dob) {
      dobError = I18n.t('MissingDOB');
      error = true;
    } else if (!Regex.validateDate(formattedDateDOB)) {
      dobError = I18n.t('InValidDate');
    } else if (!isOverEighteen(formattedDateDOB)) {
      dobError = I18n.t('userMustBe18Plus');
    }

    if (!imagePath) {
      panImageError = I18n.t('MissingPAN_Image');
      error = true;
    } else if (imagePath && bytesToMegaBytes(imagePath.size) > 2) {
      panImageError = I18n.t('ImageSizeMessage');
      error = true;
    }

    if (error) {
      setNameError(nameError);
      setPanNumberError(panNumberError);
      setDOBError(dobError);
      setPanImageError(panImageError);
      if (dob === null) {
        setDOB('');
      }
    } else {
      updatePAN_detailProceed(formattedDateDOB);
    }
  };

  const container = {
    borderWidth: status === 'verified' ? 1.5 : 0,
    backgroundColor: theme.verifyAccountItemContainer,
    borderColor: theme.verifyAccountItemContainerBorder
  };

  const headerContainer = {
    backgroundColor: theme.verifyAccountItemHeaderContainer
  };

  const headerTitle = {
    color: theme.verifyAccountItemTitle
  };

  const headerVerified = {
    color: theme.verifyAccountItemVerified
  };

  const headerPending = {
    color: theme.verifyAccountItemPending
  };

  const headerRejected = {
    color: theme.verifyAccountItemRejected
  };

  const verifyBtn = {
    color: theme.verifyAccountItemBtnVerifyBtnLabel,
    backgroundColor: theme.verifyAccountItemBtnVerifyBtnBG,
    borderColor: theme.verifyAccountItemBtnVerifyBtnBorder
  };

  const textInputBG = {
    backgroundColor: theme.verifyAccountItemInputBG,
    color: theme.verifyAccountItemInputText
  };

  const bottomBtn = {
    color: theme.verifyAccountItemBottomBtnLabel,
    backgroundColor: theme.verifyAccountItemBottomBtnBG,
    borderColor: theme.verifyAccountItemBottomBtnBorder
  };

  const contentWrapper = {
    backgroundColor: theme.verifyAccountItemContent
  };

  const infoTitle = {
    color: theme.verifyAccountItemInfoText
  };

  const errorText = {
    color: theme.verifyAccountItemErrorText
  };

  const imageUploadBtn = {
    color: theme.verifyAccountItemImageUploadBtnLabel,
    backgroundColor: theme.verifyAccountItemImageUploadBtn,
    borderColor: theme.verifyAccountItemImageUploadBtnBorder
  };

  const inputSx: SxProps<Theme> = {
    '& .MuiInputBase-input': {
      color: theme.verifyAccountItemInputText,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'uppercase'
    },
    '& .MuiFormHelperText-root.Mui-error': {
      backgroundColor: theme.verifyAccountItemContent,
      margin: 0,
      color: theme.verifyAccountItemErrorText,
      paddingLeft: '5px',
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root': {
      color: theme.verifyAccountItemInputText,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.verifyAccountItemInputText,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& input::placeholder': {
      color: theme.verifyAccountItemInputPlaceHolder,
      fontSize: '1.1rem',
      textTransform: 'none'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.verifyAccountItemInputText
      },
      '&:hover fieldset': {
        borderColor: theme.verifyAccountItemInputText
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.verifyAccountItemInputText,
        borderWidth: '1px'
      }
    }
  };

  return (
    <Card className={'verify-pan-item-container'} style={container}>
      <View className={'verify-pan-item-header-container'} style={headerContainer}>
        <View className={'verify-pan-item-logo-title-wrapper'}>
          <Constants.Svgs.Pancard
            fillPrimary={theme.svgPrimary}
            fillSecondary={theme.svgSecondary}
          />
          <Text className={'verify-pan-item-title'} style={headerTitle}>
            {'Pancard'}
          </Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          {(status === 'rejected' || status === 'inReview') && (
            <Button
              style={verifyBtn}
              className={'verify-pan-item-verify-btn'}
              onClick={() => onPressDelete(_id)}>
              {'Delete'}
            </Button>
          )}

          {isNeedToAdd && (
            <Button
              style={verifyBtn}
              className={'verify-pan-item-verify-btn'}
              onClick={() => setExpand(!expand)}>
              {'Add'}
            </Button>
          )}

          {number.length > 0 &&
            (status === 'verified' ? (
              <View className={'verify-pan-item-logo-title-wrapper'}>
                <Constants.Svgs.VerificationCheck
                  width={25}
                  height={25}
                  fillPrimary={theme.profileIndicatorCheckedPrimary}
                  fillSecondary={theme.profileIndicatorCheckedSecondary}
                />
                <Text className={'verify-pan-item-verified'} style={headerVerified}>
                  {'Verified'}
                </Text>
              </View>
            ) : status === 'rejected' ? (
              <View className={'verify-pan-item-logo-title-wrapper'}>
                <Text className={'verify-pan-item-verified'} style={headerRejected}>
                  {'Rejected'}
                </Text>
              </View>
            ) : (
              <View className={'verify-pan-item-logo-title-wrapper'}>
                <Text className={'verify-pan-item-verified'} style={headerPending}>
                  {'Pending'}
                </Text>
              </View>
            ))}
        </View>
      </View>
      {number.length > 0 && <PanDetails item={item} />}
      {expand && (
        <View className={'verify-pan-item-content-wrapper '} style={contentWrapper}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center'
            }}>
            {imagePath != '' ? (
              <img
                key={imagePath}
                src={URL.createObjectURL(imagePath)}
                style={{ width: '100px', height: '100px' }}
              />
            ) : (
              <Constants.Svgs.PANCardUpload fill={theme.verifyAccountItemImageUploadIcon} />
            )}
            {!imagePath ? (
              <label htmlFor="upload-photo">
                <input
                  style={{ display: 'none' }}
                  id="upload-photo"
                  name="upload-photo"
                  type="file"
                  accept="image/*"
                  onChange={onChangeUploadImage}
                />
                <Button
                  style={imageUploadBtn}
                  className={'verify-pan-item-image-upload-wrapper'}
                  variant="contained"
                  component="span">
                  {'Upload Pancard Image (Max Size 2mb)'}
                </Button>
              </label>
            ) : (
              <Button
                style={imageUploadBtn}
                onClick={() => {
                  setImagePath('');
                }}
                className={'verify-pan-item-image-upload-wrapper'}
                variant="contained"
                component="span">
                {'Remove Pancard Image'}
              </Button>
            )}
          </View>
          {panImageError && (
            <Text
              className={'verify-pan-item-error-text'}
              style={errorText}>{`${panImageError}`}</Text>
          )}
          <View style={{ width: '80%' }}>
            <TextField
              ref={refName}
              style={textInputBG}
              placeholder={I18n.t('NameOnPANCard')}
              value={name}
              className={'verify-pan-item-text-input'}
              label={I18n.t('NameOnPANCard')}
              variant="outlined"
              size={'small'}
              onChange={onChangeName}
              sx={inputSx}
              error={nameError ? true : false}
              helperText={nameError}
            />

            <TextField
              ref={refCardNumber}
              style={textInputBG}
              placeholder={I18n.t('PAN_CardNumber')}
              value={panNumber}
              className={'verify-pan-item-text-input'}
              label={I18n.t('PAN_CardNumber')}
              variant="outlined"
              size={'small'}
              onChange={onChangePanNumber}
              sx={inputSx}
              error={panNumberError ? true : false}
              helperText={panNumberError}
              inputProps={{ maxLength: 10 }}
              autoCapitalize="characters"
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                ref={refDOB}
                label={I18n.t('DateOfBirth') + ' (YYYY-MM-DD)'}
                inputFormat="yyyy-MM-dd"
                value={dob}
                onChange={handleDatePicked}
                maxDate={minDOB}
                renderInput={(params: any) => (
                  <TextField
                    placeholder={I18n.t('DateOfBirth') + ' (YYYY-MM-DD)'}
                    className={'verify-pan-item-text-input'}
                    style={textInputBG}
                    size="small"
                    sx={inputSx}
                    error={dobError ? true : false}
                    helperText={dobError}
                    {...params}
                  />
                )}
              />
            </LocalizationProvider>
            <Button
              onClick={onPressSubmitButton}
              style={bottomBtn}
              className={'verify-pan-item-bottom-btn'}>
              {I18n.t('SubmitForVerification')}
            </Button>

            <Text className={'verify-pan-item-info-title'} style={infoTitle}>
              {'* Name on Profile, PAN and Bank Acc should be same.'}
            </Text>
          </View>
        </View>
      )}
    </Card>
  );
};
export default React.memo(VerifyPanItem);
